import apiClient from '../utils/apiClient';
import { CardForTrade, Card, Product, InsertSet } from 'shared/models';

const API_URL = '/api/card-for-trade';

const tradeService = {
  // Get products for cards a user has for trade with pagination
  getCardsForTradeProductsByPage: async (userId, page = 1, limit = 20, sortBy = null) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const params = {
      page,
      limit
    };
    
    // Add optional sort parameter if provided
    if (sortBy) {
      params.sortBy = JSON.stringify(sortBy);
    }
    
    const response = await apiClient.get(`${API_URL}/products/user/${userId}`, { 
      params
    });
    
    return {
      products: (response.data.products || []).map(product => new Product(product)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalItems: response.data.totalProducts || 0
    };
  },
  
  // Get cards for trade statistics for a user
  getUserCardsForTradeStats: async (userId) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const response = await apiClient.get(`${API_URL}/stats/user/${userId}`);
    
    return {
      totalCards: response.data.totalCards || 0,
      totalProducts: response.data.totalProducts || 0,
      totalInsertSets: response.data.totalInsertSets || 0
    };
  },

  // Get insert sets for cards a user has for trade by product
  getUserCardsForTradeInsertSetsByProduct: async (userId, productId, page = 1, limit = 20) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    if (!productId) {
      throw new Error('Product ID is required');
    }
    
    const response = await apiClient.get(`${API_URL}/insertSets/user/${userId}/product/${productId}`, {
      params: {
        page,
        limit
      }
    });
    
    return {
      insertSets: (response.data.insertSets || []).map(insertSet => new InsertSet(insertSet)),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalInsertSets: response.data.totalInsertSets || 0
    };
  },

  // Get cards a user has for trade filtered by insert set
  getUserCardsForTradeByInsertSet: async (userId, insertSetId, page = 1, limit = 20) => {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    if (!insertSetId) {
      throw new Error('Insert Set ID is required');
    }
    
    const response = await apiClient.get(`${API_URL}/cards/user/${userId}/insert-set/${insertSetId}`, {
      params: {
        page,
        limit
      }
    });
    
    return {
      cardsForTrade: (response.data.cardsForTrade || []),
      currentPage: response.data.currentPage || page,
      totalPages: response.data.totalPages || 1,
      totalItems: response.data.totalItems || 0
    };
  },

  // Get a specific card for trade by card ID
  getCardForTradeByCardId: async (cardId) => {
    if (!cardId) {
      throw new Error('Card ID is required');
    }
    
    const response = await apiClient.get(`${API_URL}/card/${cardId}`);
    
    return response.data.cardForTrade || null;
  },

  // Add a card to trade list - simplified API
  addCardForTrade: async (card, notes = '') => {
    if (!card || !card.id) {
      throw new Error('Invalid card provided');
    }
    
    // Direct API call with minimal data
    // Note: productId and insertSetId are automatically populated on the backend
    const response = await apiClient.post(API_URL, {
      card: card.id,
      notes
    });
    
    return response.data;
  },
  
  // Remove a card from trade list - simplified API
  removeCardFromTrade: async (card) => {
    if (!card || !card.id) {
      throw new Error('Invalid card provided');
    }
    
    // Direct API call to card-based endpoint
    const response = await apiClient.delete(`${API_URL}/card/${card.id}`);
    return response.data;
  },
  
  // Add all cards from an insert set to trade list
  addInsertSetForTrade: async (insertSetId, option, quantityThreshold = 1) => {
    if (!insertSetId) {
      throw new Error('Insert Set ID is required');
    }
    
    // Valid options: 'all', 'collection', 'quantity'
    if (!['all', 'collection', 'quantity'].includes(option)) {
      throw new Error('Invalid option provided');
    }
    
    // Direct API call with insert set ID and option
    const response = await apiClient.post(`${API_URL}/insert-set/${insertSetId}`, {
      option,
      quantityThreshold: parseInt(quantityThreshold) || 1
    });
    
    return response.data;
  }
};

export default tradeService;